import { z } from 'zod';

import type { DraftFields, InterviewOut, Score, ScoreDraftFields } from '@/api/interviews';

import {
  isRatingChoice,
  NOTHING_CHOSEN,
  RATING_CHOICES,
  VERDICT_CHOICES,
  type RatingChoice,
} from './scoreOptions';

/**
 * **§6.6's form, as one schema** — the shape `.claude/rules/frontend.md` asks for (*"the zod schema
 * defines the form type"*), in the shape `questionFields.ts` and `personFields.ts` established for
 * §6.9 and §6.4.
 *
 * ⚠️ **It is the *form's* shape and deliberately not the API's.** The payload is the server's answer
 * and the only source of the questions: `question_text_snapshot`, `position`,
 * `question_has_been_edited` and every id but `question_id` are absent here, because none of them is
 * writable through any request body in this API and a save must never touch them (ADR 0004 — the
 * snapshot is the record of what was asked). What is here is exactly the two things an interviewer can
 * change per question, plus the two the Overall block holds.
 *
 * ⚠️ **It refuses nothing a person can type, and that is the design rather than an omission.** §6.6:
 * the comment requirement at 1, 2 and 5 is *"enforced on submit, not on every keystroke, so nobody is
 * nagged mid-thought"*, and the autosave accepts a 1 with no comment by design (ADR 0007 — a draft is
 * whatever has been written so far, and refusing to keep it is the loss the autosave exists to
 * prevent). So there is no `.min(1)` and no `.refine` in this file: the completeness gate belongs to
 * the act that is irreversible, which is TASK-084's submission. What the schema does do is fix the
 * **type** — a rating outside the five and a verdict outside the four are unrepresentable, so no
 * control can put one into the form and no later save can send one.
 */

/** One question, as the form holds it: the id the autosave addresses it by, and the two writable fields. */
const scoreFieldsSchema = z.object({
  /**
   * `ScoreUpdate` addresses a score by `question_id` and not by the score's own `id` (`api/interviews.ts`),
   * so that is the key the form carries — the one field here that is neither typed into nor chosen.
   */
  question_id: z.number(),
  rating: z.enum(RATING_CHOICES),
  comment: z.string(),
});

export const fillingInSchema = z.object({
  /**
   * ⚠️ **An array, in the payload's own order, and nothing sorts or filters it** — feedback protection
   * #2 at this boundary (ADR 0005). The order **is** the frozen bank order (ADR 0004), and a score
   * whose bank question has since been archived is still part of *this* conversation and still writable
   * on purpose (backend TASK-031). A `Record` keyed by question id would have made the order this
   * client's to choose; an array cannot.
   */
  scores: z.array(scoreFieldsSchema),
  overall_recommendation: z.enum(VERDICT_CHOICES),
  overall_comment: z.string(),
});

export type FillingInForm = z.infer<typeof fillingInSchema>;

/**
 * A rating from the payload as the scale holds it.
 *
 * `null` is *nothing chosen* — the ordinary state of an unanswered question, and §6.6 permits skipping
 * one (*"a skipped question is information"*). A number outside the five is *nothing chosen* too: the
 * API validates `1..5`, so it cannot arrive, and if it ever did, rendering it as one of the five
 * buttons would be the client claiming to have understood a value it cannot name. Nothing is lost by
 * it: the autosave diffs against the **payload**, so a value this form does not hold is a value it
 * also never sends.
 */
const ratingChoiceOf = (rating: Score['rating']): RatingChoice => {
  if (rating === null) return NOTHING_CHOSEN;
  const choice = String(rating);
  return isRatingChoice(choice) ? choice : NOTHING_CHOSEN;
};

/**
 * The draft as the server currently holds it, in the form's shape — §6.6's `defaultValues`.
 *
 * ⚠️ **`interview.scores` is mapped and never filtered, sliced or sorted**, which is the whole of
 * protection #2 on this screen: the list rendered has the same length as the list received, always.
 * An undecided draft's verdict is `null` on the wire — the document has typed the field
 * `OverallRecommendation | null` since backend TASK-104, so the blank string this once had to narrow
 * at the API edge no longer arrives — and here it is *nothing chosen*, never a verdict.
 */
export const formFieldsOf = (interview: InterviewOut): FillingInForm => ({
  scores: interview.scores.map((score) => ({
    question_id: score.question_id,
    rating: ratingChoiceOf(score.rating),
    comment: score.comment,
  })),
  overall_recommendation: interview.overall_recommendation ?? NOTHING_CHOSEN,
  overall_comment: interview.overall_comment,
});

/**
 * **The form as the autosave compares it — the inverse of `formFieldsOf`, and the edge where a
 * *control's* vocabulary becomes the *API's*.**
 *
 * ⚠️ **This is where *nothing chosen* stops being a value.** A radio holds the empty string while
 * nothing is selected (`NOTHING_CHOSEN`, which is Radix's own state and not a sentinel of ours), and
 * the API has no way to say *nothing*: an explicit `null` is a 422 by design and `''` is not one of the
 * four verdicts. So both become `null` **here**, and `draftChanges` — which owns the rule — omits the
 * field rather than sending it (`api/interviews.ts`). The two halves are deliberately in different
 * modules: this one translates, that one decides what a translation is allowed to send.
 *
 * ⚠️ **Keyed by `question_id`, and the order is dropped on purpose.** `DraftFields.scores` is a map
 * because it is only ever *compared*, never rendered — the rendered list keeps the payload's own order
 * and nothing here touches it (protection #2, ADR 0005). A question the payload sent twice cannot
 * happen (the server's own 422 covers it), and if it did, the later entry would win a comparison rather
 * than produce two bodies.
 */
export const draftFieldsOfForm = (values: FillingInForm): DraftFields => {
  const scores: Record<number, ScoreDraftFields> = {};
  for (const score of values.scores) {
    scores[score.question_id] = {
      rating: score.rating === NOTHING_CHOSEN ? null : Number(score.rating),
      comment: score.comment,
    };
  }

  return {
    overall_recommendation:
      values.overall_recommendation === NOTHING_CHOSEN ? null : values.overall_recommendation,
    overall_comment: values.overall_comment,
    scores,
  };
};
